import { useMemo, useEffect, useCallback, useState } from 'react';
import {
  RegisterOptions,
  useFormContext,
} from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import {
  MAX_PAGE_SIZE,
  useDepartmentSearch,
  ISearchParams,
} from 'src/utilities/useSearchHooks';
import { DropdownOptionProps } from 'src/components/CookbookSelectWrapper/CookbookSelectWrapper';
import { useFetchDepartment } from 'src/components/common/hooks/useFetchDepartment/useFetchDepartment';
import {
  combineDepartments,
  transformToOptions,
} from 'src/components/common/DepartmentsSelectSearchControl/hooks/useDepartmentsSelectSearchControl.utils';

export type UseDepartmentsSelectSearchControlReturnType = {
  options: DropdownOptionProps[];
  handleSearchChange: (query?: string) => Promise<void>;
  isLoadingFetchedDepartment: boolean;
};

export type UseDepartmentsSelectSearchControlProps = {
  name: string;
  rules?: RegisterOptions;
};

export const useDepartmentsSelectSearchControl = ({
  name,
  rules,
}: UseDepartmentsSelectSearchControlProps): UseDepartmentsSelectSearchControlReturnType => {
  const { corpid: corpId } = useParams<{ corpid: string }>();
  const { watch } = useFormContext();
  const departmentId = watch(name);
  const [hasSearchQuery, setHasSearchQuery] = useState<boolean>(false);
  const isRequired = Boolean(rules?.required);
  const { doSearch, resultsList, unsubscribeFromSearch } = useDepartmentSearch(
    corpId,
    isRequired
  );
  const {
    department: fetchedDepartment,
    fetchDepartment,
    isLoading: isLoadingFetchedDepartment,
  } = useFetchDepartment(corpId);

  useEffect(() => {
    if (departmentId) {
      fetchDepartment(departmentId);
    }
  }, [departmentId, fetchDepartment]);

  const transformedOptions = useMemo((): DropdownOptionProps[] => {
    const allDepartments = combineDepartments({
      resultsList,
      fetchedDepartment,
      hasSearchQuery,
    });
    return transformToOptions({ departments: allDepartments });
  }, [fetchedDepartment, resultsList, hasSearchQuery]);

  const handleSearchChange = useCallback((query?: string): Promise<void> => {
    const trimmedQuery = query?.trim();
    setHasSearchQuery(Boolean(trimmedQuery));

    const searchParams: ISearchParams = { size: MAX_PAGE_SIZE };
    if (trimmedQuery) {
      searchParams.query = trimmedQuery;
    }

    return doSearch(searchParams);
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      unsubscribeFromSearch();
    };
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
  }, []);

  return {
    options: transformedOptions,
    handleSearchChange,
    isLoadingFetchedDepartment,
  };
};
